import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Cookie, X } from 'lucide-react';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Small delay for better UX - don't show immediately on page load
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setIsVisible(false);
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 px-3 sm:px-4 pb-2 sm:pb-3 animate-slide-up pointer-events-none"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)' }}
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-3xl mx-auto pointer-events-auto">
        <div className="bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 shadow-2xl shadow-black/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 sm:gap-3">
            {/* Icon and Text */}
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-slate-300 text-[11px] sm:text-[12.5px] leading-snug">
                  <span className="text-white font-semibold">We value your privacy.</span>{' '}
                  Cookies help us improve your experience.{' '}
                  <Link 
                    to="/privacy-policy" 
                    className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
                  >
                    Learn more
                  </Link>
                  <span className="hidden sm:inline text-slate-400 ml-2">· GDPR & CCPA Compliant</span>
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDecline}
                className="flex-1 sm:flex-none border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white text-[11px] sm:text-xs h-7 sm:h-8 px-3"
                data-testid="cookie-decline-btn"
              >
                Decline
              </Button>
              <Button
                size="sm"
                onClick={handleAccept}
                className="flex-1 sm:flex-none bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] sm:text-xs h-7 sm:h-8 px-3"
                data-testid="cookie-accept-btn"
              >
                Accept All
              </Button>
              <button
                onClick={handleClose}
                className="p-1 text-slate-400 hover:text-white transition-colors rounded-md hover:bg-slate-800 hidden sm:flex"
                aria-label="Close"
                data-testid="cookie-close-btn"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default CookieConsent;
